// src/myNavbar/AppSidebar.js
// The signed-in app's only navigation (no top bar): logo + collapse toggle, the main sections,
// Plans / Settings / Help, minutes left, and the account row. Rendered once by AuthLayout.
//   ≥1280px: expanded sidebar or icon rail, remembered across visits (App's isSidebarOpen).
//   <1280px: always the icon rail; the toggle opens the expanded sidebar as an overlay drawer.

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { AuthContext } from '../AuthContext';
import { unregisterAllListeners } from '../components/appComponents/ListenerManager';
import PlanPurchasePage from '../myPlans/PlanPurchasePage';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleQuestion,
  faClapperboard,
  faCode,
  faComment,
  faCrown,
  faGear,
  faMagnifyingGlass,
  faRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import bumpups_logo from '../assets/images/default/bumpups-logo-final.png';
import placeholder_profile from '../assets/images/profile/placeholder-profile.jpg';
import './appSidebar.css';

const NARROW_QUERY = '(max-width: 1279px)';

const NAV_ITEMS = [
  { to: '/workspace', label: 'Workspace', icon: faComment },
  { to: '/ask', label: 'Ask your videos', icon: faMagnifyingGlass },
  { to: '/bumper', label: 'Creator Studio', icon: faClapperboard },
  { to: '/api', label: 'API', icon: faCode, pro: true },
];

const PanelIcon = () => (
  <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
    <rect x="2.75" y="3.75" width="14.5" height="12.5" rx="2.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7.75 3.75v12.5" stroke="currentColor" strokeWidth="1.5" />
  </svg>
);

function useIsNarrow() {
  const [isNarrow, setIsNarrow] = useState(() => window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const onChange = e => setIsNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isNarrow;
}

// Live plan + usage from users/{uid}/subscriptionData/planDetails
function usePlanDetails(uid) {
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(db, 'users', uid, 'subscriptionData', 'planDetails'), snap => {
      const d = snap.data() || {};
      setPlan({
        name: (d.currentPlan || 'free').toLowerCase(),
        used: d.creditsUsed ?? 0,
        limit: d.creditsLimit ?? 0,
        cycleEnd: d.quotaCycleEnd?.toDate() || null,
      });
    }, () => {});
  }, [uid]);
  return plan;
}

// Avatar and the optional name from Settings
function useProfile(uid) {
  const [profile, setProfile] = useState({ image: '', name: '' });
  useEffect(() => {
    if (!uid) return;
    return onSnapshot(doc(db, 'users', uid, 'userSettings', 'profile'), snap => {
      const d = snap.data() || {};
      setProfile({ image: d.profileImage || '', name: (d.displayName || '').trim() });
    }, () => {});
  }, [uid]);
  return profile;
}

const planLabel = name => (name === 'pro' ? 'Pro' : name === 'free' ? 'Free' : name ? name[0].toUpperCase() + name.slice(1) : '');

const resetLabel = cycleEnd => {
  if (!cycleEnd) return '';
  // The daily reset job rolls cycles shortly after they end
  if (cycleEnd <= new Date()) return 'Resets soon';
  return `Resets ${cycleEnd.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
};

export default function AppSidebar({ isOpen, onToggle }) {
  const { currentUser } = useContext(AuthContext);
  const user = currentUser || auth.currentUser;
  const location = useLocation();
  const navigate = useNavigate();
  const isNarrow = useIsNarrow();
  const plan = usePlanDetails(user?.uid);
  const { image: profileImage, name: displayName } = useProfile(user?.uid);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showPlans, setShowPlans] = useState(false);
  const accountRef = useRef(null);

  const expanded = isNarrow ? drawerOpen : isOpen;
  const isDrawer = isNarrow && drawerOpen;

  // Any navigation closes the drawer and the account menu
  useEffect(() => {
    setDrawerOpen(false);
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isNarrow) setDrawerOpen(false);
  }, [isNarrow]);

  // The account menu only exists on the rail
  useEffect(() => setMenuOpen(false), [expanded]);

  // Close the account menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return;
    const onPointer = e => { if (!accountRef.current?.contains(e.target)) setMenuOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const toggle = () => (isNarrow ? setDrawerOpen(v => !v) : onToggle());

  const logout = async () => {
    setMenuOpen(false);
    unregisterAllListeners();
    await auth.signOut();
    navigate('/');
  };

  const openPlans = () => {
    setMenuOpen(false);
    setDrawerOpen(false);
    setShowPlans(true);
  };

  const minutesLeft = plan ? Math.max(plan.limit - plan.used, 0) : null;
  const fillPct = plan?.limit ? Math.min((minutesLeft / plan.limit) * 100, 100) : 0;
  const isFree = plan?.name === 'free';

  return (
    <>
      {/* keeps the rail's space in the page while the drawer floats over it */}
      {isDrawer && <div className="asb-spacer" />}
      {isDrawer && <div className="asb-backdrop" onClick={() => setDrawerOpen(false)} />}

      <aside className={`asb${expanded ? ' is-expanded' : ' is-rail'}${isDrawer ? ' is-drawer' : ''}`}>
        <div className="asb-header">
          <button
            type="button"
            className="asb-icon-btn"
            onClick={toggle}
            aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
            title={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          >
            <PanelIcon />
          </button>
          {expanded && (
            <Link to="/workspace" className="asb-logo-link" aria-label="Bumpups home">
              <img src={bumpups_logo} alt="Bumpups" className="asb-logo" />
            </Link>
          )}
        </div>

        <nav className="asb-nav" aria-label="Main">
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `asb-link${isActive ? ' active' : ''}`}
              title={expanded ? undefined : item.label}
              aria-label={item.label}
            >
              <FontAwesomeIcon icon={item.icon} className="asb-link-icon" />
              {expanded && <span className="asb-link-text">{item.label}</span>}
              {expanded && item.pro && isFree && <span className="asb-pro-badge">Pro</span>}
            </NavLink>
          ))}

          <div className="asb-nav-divider" />

          <button
            type="button"
            className="asb-link"
            onClick={openPlans}
            title={expanded ? undefined : 'Plans'}
            aria-label="Plans"
          >
            <FontAwesomeIcon icon={faCrown} className="asb-link-icon" />
            {expanded && <span className="asb-link-text">Plans</span>}
          </button>
          <NavLink
            to="/settings"
            className={({ isActive }) => `asb-link${isActive ? ' active' : ''}`}
            title={expanded ? undefined : 'Settings'}
            aria-label="Settings"
          >
            <FontAwesomeIcon icon={faGear} className="asb-link-icon" />
            {expanded && <span className="asb-link-text">Settings</span>}
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) => `asb-link${isActive ? ' active' : ''}`}
            title={expanded ? undefined : 'Help'}
            aria-label="Help"
          >
            <FontAwesomeIcon icon={faCircleQuestion} className="asb-link-icon" />
            {expanded && <span className="asb-link-text">Help</span>}
          </NavLink>
        </nav>

        <div className="asb-bottom">
          {expanded && plan && (
            // The whole card opens the plans popup
            <button type="button" className="asb-usage" onClick={openPlans} aria-label="View plans">
              <span className="asb-usage-row">
                <span className="asb-usage-plan">{planLabel(plan.name)} plan</span>
                {isFree && <span className="asb-upgrade">Upgrade</span>}
              </span>
              <span className="asb-usage-minutes">
                {minutesLeft.toLocaleString()} <span className="asb-usage-total">/ {plan.limit.toLocaleString()} minutes left</span>
              </span>
              <span className="asb-usage-track">
                <span className="asb-usage-fill" style={{ width: `${fillPct}%` }} />
              </span>
              {plan.cycleEnd && <span className="asb-usage-reset">{resetLabel(plan.cycleEnd)}</span>}
            </button>
          )}

          {expanded ? (
            <div className="asb-account-row">
              {/* avatar + name open Settings, where both are edited */}
              <Link to="/settings" className="asb-account-link" title={user?.email}>
                <img src={profileImage || placeholder_profile} alt="" className="asb-avatar" />
                <span className="asb-account-name">{displayName || user?.email}</span>
              </Link>
              <button type="button" className="asb-logout" onClick={logout} aria-label="Log out" title="Log out">
                <FontAwesomeIcon icon={faRightFromBracket} />
              </button>
            </div>
          ) : (
            // The rail has no room for the minutes card or who's signed in, so the avatar opens them
            <div className="asb-account" ref={accountRef}>
              {menuOpen && (
                <div className="asb-menu" role="menu">
                  <div className="asb-menu-usage">
                    <Link to="/settings" className="asb-menu-profile" role="menuitem" onClick={() => setMenuOpen(false)} title="Settings">
                      {displayName && <div className="asb-menu-name">{displayName}</div>}
                      <div className="asb-menu-email">{user?.email}</div>
                    </Link>
                    {plan && <div>{planLabel(plan.name)} plan · {minutesLeft.toLocaleString()} / {plan.limit.toLocaleString()} minutes left</div>}
                  </div>
                  <button type="button" className="asb-menu-item" role="menuitem" onClick={logout}>
                    <FontAwesomeIcon icon={faRightFromBracket} className="asb-menu-icon" /> Log out
                  </button>
                </div>
              )}
              <button
                type="button"
                className={`asb-account-btn${menuOpen ? ' is-open' : ''}`}
                onClick={() => setMenuOpen(v => !v)}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Account menu"
                title="Account"
              >
                <img src={profileImage || placeholder_profile} alt="" className="asb-avatar" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {showPlans && <PlanPurchasePage source="sidebar" onClose={() => setShowPlans(false)} />}
    </>
  );
}
